import TextField from '@mui/material/TextField';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import type { PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import { useTranslation } from '@tyro/i18n';
import dayjs, { type Dayjs } from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import LocalizedFormat from 'dayjs/plugin/localizedFormat';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDisclosure } from '../../hooks/use-disclosure';
import { wasMultiSelectKeyUsed } from '../../utils';
import { MultiDatePickerDay } from './picker-day';
import { DatePickerPopper } from './popper';
import type { MultiDatePickerProps } from './types';
import { dayKeyFormat } from './use-day-settings';

export type { MultiDatePickerProps };

dayjs.extend(LocalizedFormat);
dayjs.extend(isSameOrBefore);

function arrayOfDatesBetween(date1: Dayjs, date2: Dayjs): Dayjs[] {
  const dates = [];
  const start = date1.isBefore(date2) ? date1 : date2;
  const end = date1.isBefore(date2) ? date2 : date1;

  let currentDate = start.clone();

  while (currentDate.isSameOrBefore(end, 'day')) {
    dates.push(currentDate.clone());
    currentDate = currentDate.add(1, 'day');
  }

  return dates;
}

export const MultiDatePicker = ({
  value = [],
  onChange,
  ...props
}: MultiDatePickerProps) => {
  const { t } = useTranslation(['common']);
  const lastSelectedDayRef = useRef<Dayjs>(undefined);
  const [rangePreviewDay, setRangePreviewDay] = useState<Dayjs>();
  const [showRangePreview, setShowRangePreview] = useState(false);
  const { isOpen, onClose, onOpen } = useDisclosure();

  const valueMap = useMemo(
    () =>
      value.reduce<Map<string, Dayjs>>((acc, day) => {
        acc.set(day.format(dayKeyFormat), day);
        return acc;
      }, new Map()),
    [value],
  );

  const onDaySelect = useCallback(
    (event: React.MouseEvent, day: Dayjs) => {
      const newValueMap = new Map(valueMap);

      if (wasMultiSelectKeyUsed(event) && lastSelectedDayRef.current) {
        const arrayOfDatesBetweenLastAndCurrent = arrayOfDatesBetween(
          lastSelectedDayRef.current,
          day,
        );
        const hasDayAlreadyBeenSelected = newValueMap.has(
          day.format(dayKeyFormat),
        );
        for (const d of arrayOfDatesBetweenLastAndCurrent) {
          if (hasDayAlreadyBeenSelected) {
            newValueMap.delete(d.format(dayKeyFormat));
          } else {
            newValueMap.set(d.format(dayKeyFormat), d);
          }
        }
      } else if (newValueMap.has(day.format(dayKeyFormat))) {
        newValueMap.delete(day.format(dayKeyFormat));
      } else {
        newValueMap.set(day.format(dayKeyFormat), day);
      }

      lastSelectedDayRef.current = day;

      onChange(Array.from(newValueMap.values()));
    },
    [valueMap],
  );

  const renderDay = useCallback(
    (pickersDayProps: PickersDayProps) => (
      <MultiDatePickerDay
        {...pickersDayProps}
        pickerValue={valueMap}
        onDaySelect={onDaySelect}
        lastSelectedDayRef={lastSelectedDayRef}
        showRangePreview={showRangePreview}
        rangePreviewDay={rangePreviewDay}
        setRangePreviewDay={setRangePreviewDay}
      />
    ),
    [
      valueMap,
      onDaySelect,
      showRangePreview,
      rangePreviewDay,
      setRangePreviewDay,
    ],
  );

  useEffect(() => {
    const shiftKeyUpHandler = (event: KeyboardEvent) => {
      if (event.key === 'Shift') {
        setShowRangePreview(false);
      }
    };
    const shiftKeyDownHandler = (event: KeyboardEvent) => {
      if (event.key === 'Shift') {
        setShowRangePreview(true);
      }
    };
    window.addEventListener('keyup', shiftKeyUpHandler);
    window.addEventListener('keydown', shiftKeyDownHandler);

    return () => {
      window.removeEventListener('keyup', shiftKeyUpHandler);
      window.removeEventListener('keydown', shiftKeyDownHandler);
    };
  }, []);

  return (
    <DatePicker
      {...props}
      open={isOpen}
      onOpen={onOpen}
      onClose={onClose}
      enableAccessibleFieldDOMStructure={false}
      slots={{
        day: renderDay,
        popper: DatePickerPopper,
        textField: TextField,
      }}
      slotProps={{
        ...props.slotProps,
        popper: {
          ...props.slotProps?.popper,
          open: isOpen,
          sx: {
            '& .MuiDayCalendar-weekContainer': {
              my: '1px',
            },
          },
          onMouseLeave: () => {
            setRangePreviewDay(undefined);
          },
        },
        field: {
          readOnly: true,
        },
        textField: {
          ...props.slotProps?.textField,
          onClick: onOpen,
          InputProps: {
            value:
              value?.length > 0
                ? t('common:dateAndXOthers', {
                    date: value[0].format('L'),
                    count: value.length - 1,
                  })
                : '',
          },
        },
      }}
    />
  );
};
